import { ChangeDetectorRef, Component, HostBinding, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from '@app/core/local-storage/local-storage.service';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import { FeeService } from '@app/core/services/FeeService';
import { FileService } from '@app/core/services/FileService';
import { GlobalMessageService } from '@app/core/services/GlobalMessageService';
import { InvoiceService } from '@app/core/services/InvoiceService';
import { MunicipalityService } from '@app/core/services/municipalityService';
import { OrganizationService } from '@app/core/services/OrganizationService';
import { UserService } from '@app/core/services/UserService';
import { environment as env } from '@env/environment';
import { AboutComponent } from '../about/about.component';

type VatAmountField = 'noVat' | 'withVat' | 'noVatMedium' | 'withVatMedium' | 'noVatLow' | 'withVatLow' | 'zeroVatAmount';
type AmountInputField = 'salary' | 'salaryExpress' | VatAmountField;

@Component({
  standalone: false,
  selector: 'anms-modern-dashboard',
  templateUrl: './modern-dashboard.component.html',
  styleUrls: ['./design-test.component.scss']
})
export class ModernDashboardComponent extends AboutComponent implements OnInit {
  private readonly sentInvoiceStatusTechnicalNames = new Set([
    'SentBailiff',
    'SentUnpaid',
    'ExpressInquirySent',
    'SentReminder',
    'SentReminder2',
    'SentToInkasso'
  ]);

  @HostBinding('class.samekonomi-theme')
  readonly isSamekonomiTheme = env.themeName === 'samfinans-theme';
  readonly year = new Date().getFullYear();
  readonly productName = env.appId === 2 ? 'SAMekonomi' : 'SAMpoolen';
  panelOpenStateCalc = false;
  vatButton = 1;
  zeroVatAmount: number;
  amountInputs: Record<AmountInputField, string> = {
    salary: '',
    salaryExpress: '',
    noVat: '',
    withVat: '',
    noVatMedium: '',
    withVatMedium: '',
    noVatLow: '',
    withVatLow: '',
    zeroVatAmount: ''
  };

  constructor(
    invoiceService: InvoiceService,
    localStorageService: LocalStorageService,
    private dashboardUserService: UserService,
    municipalityService: MunicipalityService,
    globalMessageService: GlobalMessageService,
    private dashboardRouter: Router,
    organizationService: OrganizationService,
    authenticationService: AuthenticationService,
    dialog: MatDialog,
    private snackBar: MatSnackBar,
    feeService: FeeService,
    fileService: FileService,
    changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute
  ) {
    super(
      invoiceService,
      localStorageService,
      dashboardUserService,
      municipalityService,
      globalMessageService,
      dashboardRouter,
      organizationService,
      authenticationService,
      dialog,
      feeService,
      fileService,
      changeDetectorRef
    );
  }

  override ngOnInit(): void {
    if (this.dashboardUserService.userHasRole('admin') && this.route.snapshot.routeConfig?.path === 'about') {
      this.dashboardRouter.navigateByUrl('/admin/task-center', { replaceUrl: true });
      return;
    }

    super.ngOnInit();
  }

  get firstName(): string {
    return this.user?.FirstName || 'välkommen';
  }

  get unreadMessageCount(): number {
    return this.globalmessages.filter(message => !message.IsRead).length;
  }

  get sentInvoiceData(): Array<any> {
    return this.invoiceData.filter(invoice =>
      this.sentInvoiceStatusTechnicalNames.has(invoice?.Status?.TechnicalName)
    );
  }

  get createInvoiceLink(): string {
    return this.isSamekonomiCompanyAdmin ? '/combined-invoice-tool' : '/invoice-tool';
  }

  get invoiceListLink(): string {
    return this.isSamekonomiCompanyAdmin ? '/organization/invoices' : '/mypages/invoices';
  }

  get createInvoiceLabel(): string {
    return this.isSamekonomiCompanyAdmin ? 'Ny samlingsfaktura' : 'Ny faktura';
  }

  get createInvoiceButtonLabel(): string {
    return this.isSamekonomiCompanyAdmin ? 'Skapa ny samlingsfaktura' : 'Skapa ny faktura';
  }

  formatAmount(value: number): string {
    if (!Number.isFinite(value)) {
      return '';
    }

    return value.toLocaleString('sv-SE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  onSalaryAmountInput(value: string, isExpress: boolean): void {
    this.amountInputs[isExpress ? 'salaryExpress' : 'salary'] = value;
    const amount = this.parseAmount(value);

    if (isExpress) {
      this.totalInvoiceAmountExpress = amount;
      void this.myExpressCalculator(amount);
      return;
    }

    this.totalInvoiceAmount = amount;
    void this.myCalculator();
  }

  onVatAmountInput(field: VatAmountField, value: string): void {
    this.amountInputs[field] = value;
    const amount = this.parseAmount(value);
    this[field] = amount;

    switch (field) {
      case 'noVat':
        this.myVatCalc(amount);
        this.amountInputs.withVat = this.formatAmount(this.withVat);
        break;
      case 'withVat':
        this.myNoVatCalc(amount);
        this.amountInputs.noVat = this.formatAmount(this.noVat);
        break;
      case 'noVatMedium':
        this.myVatCalcMedium(amount);
        this.amountInputs.withVatMedium = this.formatAmount(this.withVatMedium);
        break;
      case 'withVatMedium':
        this.myNoVatCalcMedium(amount);
        this.amountInputs.noVatMedium = this.formatAmount(this.noVatMedium);
        break;
      case 'noVatLow':
        this.myVatCalcLow(amount);
        this.amountInputs.withVatLow = this.formatAmount(this.withVatLow);
        break;
      case 'withVatLow':
        this.myNoVatCalcLow(amount);
        this.amountInputs.noVatLow = this.formatAmount(this.noVatLow);
        break;
    }
  }

  formatEditableAmount(field: AmountInputField, value: number): void {
    this.amountInputs[field] = this.formatAmount(value);
  }

  async copyReferralCode(): Promise<void> {
    const referralCode = this.user?.ReferralCode;
    if (!referralCode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(referralCode));
      this.snackBar.open('Värvningskoden har kopierats', '', { duration: 2500 });
    }
    catch {
      this.snackBar.open('Det gick inte att kopiera värvningskoden', '', { duration: 3500 });
    }
  }

  private parseAmount(value: string): number {
    const compactValue = String(value ?? '').replace(/[\s\u00a0\u202f]/g, '');
    const normalizedValue = compactValue.includes(',')
      ? compactValue.replace(/\./g, '').replace(',', '.')
      : compactValue;
    const amount = Number(normalizedValue);

    return normalizedValue !== '' && Number.isFinite(amount) ? amount : undefined;
  }

  private get isSamekonomiCompanyAdmin(): boolean {
    return this.appId === 2 && this.dashboardUserService.userHasRole('companyadmin');
  }
}
